import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/apiClient";
import { Lock, CheckCircle2, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <main className="bg-onyx min-h-screen pt-16 flex items-center grain" data-testid="reset-password-page">
      <div className="max-w-md mx-auto w-full px-5 py-20">
        <div className="text-center mb-10">
          <Lock className="text-gold mx-auto mb-4" size={40} strokeWidth={1.5} />
          <h1 className="font-serif text-4xl text-bone">Set New Password</h1>
          <p className="text-bone/50 mt-2">Choose a strong password of at least 8 characters.</p>
        </div>
        {!token ? (
          <div className="bg-surface border border-white/10 p-8 text-center" data-testid="reset-invalid">
            <p className="text-red-400">This reset link is invalid or incomplete. Please request a new one.</p>
            <Link to="/forgot-password" className="inline-flex items-center gap-2 text-gold hover:text-gold2 mt-6 text-sm">
              <ArrowLeft size={16} /> Request a new link
            </Link>
          </div>
        ) : done ? (
          <div className="bg-surface border border-white/10 p-8 text-center" data-testid="reset-success">
            <CheckCircle2 className="text-gold mx-auto mb-4" size={36} />
            <p className="text-bone/80">Your password has been reset. Redirecting you to sign in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-surface border border-white/10 p-8 space-y-5" data-testid="reset-password-form">
            <div>
              <label className="text-xs uppercase tracking-widest text-bone/60 mb-2 block">New Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} data-testid="reset-password"
                className="w-full bg-black/50 border border-white/10 px-4 py-3 text-bone focus:outline-none focus:border-gold/50" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-bone/60 mb-2 block">Confirm New Password</label>
              <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="reset-confirm"
                className="w-full bg-black/50 border border-white/10 px-4 py-3 text-bone focus:outline-none focus:border-gold/50" />
            </div>
            {error && <p className="text-red-400 text-sm" data-testid="reset-error">{error}</p>}
            <button type="submit" disabled={loading} data-testid="reset-submit"
              className="w-full bg-gold text-black py-3 font-medium hover:bg-gold2 transition-colors disabled:opacity-60">
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}
